import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Building2, Check, Loader2 } from "lucide-react";
import type { AccountRole, CreateUserFormData } from "./types";
import { ACCOUNT_ROLE_LABELS, ALL_ACCOUNT_ROLES } from "./types";
import { validateUsername } from "../../lib/username-validation";
import { OrgCombobox } from "./OrgCombobox";
import { CoTeachingPicker } from "./CoTeachingPicker";
import type { CoTeacher } from "./coteaching-api";
import { GradeSelect } from "./GradeSelect";

type CreateUserDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CreateUserFormData;
  onFormDataChange: (data: CreateUserFormData) => void;
  onSubmit: (event: React.FormEvent) => void;
  isCreating: boolean;
};

const describeRole = (role: AccountRole): string => {
  switch (role) {
    case "ORG_ADMIN":
      return "Create an org admin and name the organization they manage.";
    case "TEACHER":
      return "Create a teacher account within an organization.";
    case "STUDENT":
      return "Create a student account within an organization.";
  }
};

export const CreateUserDialog = ({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  isCreating,
}: CreateUserDialogProps) => {
  const [usernameError, setUsernameError] = useState("");
  const [organizationError, setOrganizationError] = useState("");
  // Selected co-teacher objects held for display; ids mirror into formData.
  const [coTeachers, setCoTeachers] = useState<CoTeacher[]>([]);

  const role = formData.account_role;
  const isOrgAdmin = role === "ORG_ADMIN";
  const isTeacher = role === "TEACHER";
  const isStudent = role === "STUDENT";
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Reset the local co-teacher selection whenever the dialog closes, the role
  // changes away from Teacher, or the chosen organization changes.
  useEffect(() => {
    if (!isOpen || !isTeacher || !formDataRef.current.organizationId) {
      if (coTeachers.length > 0) setCoTeachers([]);
      const current = formDataRef.current;
      if ((current.coTeacherIds?.length ?? 0) > 0) {
        onFormDataChange({ ...current, coTeacherIds: [] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTeacher, formData.organizationId]);

  const handleUsernameChange = (value: string) => {
    onFormDataChange({ ...formData, username: value });
    if (usernameError) {
      setUsernameError(validateUsername(value, false));
    }
  };

  const handleUsernameBlur = () => {
    if (formData.username) {
      setUsernameError(validateUsername(formData.username, false));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isStudent && !formData.organizationId?.trim()) {
      setOrganizationError("Select an organization for the student.");
      return;
    }
    setOrganizationError("");
    if (!isOrgAdmin && formData.username) {
      const err = validateUsername(formData.username);
      setUsernameError(err);
      if (err) return;
    }
    onSubmit(event);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create {ACCOUNT_ROLE_LABELS[role]}</DialogTitle>
          <DialogDescription>{describeRole(role)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* User Type selector */}
          <div className="space-y-2 sm:col-span-2">
            <Label>User Type</Label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_ACCOUNT_ROLES.map((opt) => {
                const active = role === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() =>
                      onFormDataChange({ ...formData, account_role: opt })
                    }
                    className={
                      "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors " +
                      (active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border/60 bg-background text-foreground hover:bg-muted/50")
                    }
                  >
                    {active ? <Check className="h-3.5 w-3.5" /> : null}
                    {ACCOUNT_ROLE_LABELS[opt]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                onFormDataChange({ ...formData, name: e.target.value })
              }
              placeholder={isOrgAdmin ? "Jane Admin" : "John Doe"}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                onFormDataChange({ ...formData, email: e.target.value })
              }
              placeholder={isOrgAdmin ? "admin@example.com" : "john@example.com"}
              required
            />
          </div>

          {!isOrgAdmin && (
            <div className="space-y-2">
              <Label htmlFor="username">
                Username{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                onBlur={handleUsernameBlur}
                placeholder="johndoe"
              />
              {usernameError ? (
                <p className="text-xs text-destructive">{usernameError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Letters, numbers, underscores, and periods only.
                  Auto-generated if left blank.
                </p>
              )}
            </div>
          )}

          {!isOrgAdmin && (
            <div className="space-y-2">
              <Label htmlFor="displayUsername">
                Display Name{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="displayUsername"
                type="text"
                value={formData.displayUsername}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    displayUsername: e.target.value,
                  })
                }
                placeholder="John D."
              />
              <p className="text-xs text-muted-foreground">
                The name shown publicly in the app.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">
              Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) =>
                onFormDataChange({ ...formData, password: e.target.value })
              }
              placeholder="••••••••"
              required
            />
          </div>

          {isStudent && (
            <div className="space-y-2">
              <Label htmlFor="grade">
                Grade{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <GradeSelect
                id="grade"
                value={formData.grade}
                onChange={(value) =>
                  onFormDataChange({ ...formData, grade: value })
                }
              />
            </div>
          )}

          {isOrgAdmin ? (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="organizationName">
                Organization Name <span className="text-destructive">*</span>
              </Label>
              <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  id="organizationName"
                  type="text"
                  value={formData.organizationName ?? ""}
                  onChange={(e) =>
                    onFormDataChange({
                      ...formData,
                      organizationName: e.target.value,
                    })
                  }
                  placeholder="Pleasant View"
                  required
                  className="h-full w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2 sm:col-span-2">
              <Label>
                Organization <span className="text-destructive">*</span>
              </Label>
              <OrgCombobox
                value={formData.organizationId}
                onChange={(organizationId) => {
                  setOrganizationError("");
                  setCoTeachers([]);
                  onFormDataChange({
                    ...formData,
                    organizationId,
                    coTeacherIds: [],
                  });
                }}
              />
              {isStudent && organizationError ? <p role="alert" className="text-xs text-destructive">{organizationError}</p> : null}
            </div>
          )}

          {isTeacher && formData.organizationId ? (
            <div className="sm:col-span-2">
              <CoTeachingPicker
                organizationId={formData.organizationId}
                selected={coTeachers}
                onChange={(next) => {
                  setCoTeachers(next);
                  onFormDataChange({
                    ...formData,
                    coTeacherIds: next.map((c) => c.teacher_id),
                  });
                }}
              />
            </div>
          ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating} className="gap-2">
              {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
              {isCreating
                ? "Creating..."
                : `Create ${ACCOUNT_ROLE_LABELS[role]}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
